import { useCallback, useMemo } from 'react'
import { useDataSource } from '../../../lib/dataSource'
import type { Task } from '../../../lib/db/models'

export interface TaskWrite {
  id: string
  changes: Partial<Omit<Task, 'id'>>
}

interface UseTaskUpdatesParams {
  /** Optimistic-first sink — visible in the next paint, not after the IDB → LiveQuery roundtrip. */
  setOptimisticTaskOverride: (taskId: string, partial: Partial<Task>) => void
  /** Prefix for the error log, so failures are attributable (e.g. `'ProjectPage'`). */
  logTag: string
}

export interface TaskUpdates {
  /** Writes per-task changes in ONE `updateTasks` — a mid-way failure leaves no partial update. */
  applyWrites: (writes: TaskWrite[]) => Promise<void>
  /**
   * Writes the same change to exactly these tasks. Setting a favourite keeps existing favourites in
   * place and appends the others in the given order (see below).
   */
  applyTo: (ids: readonly string[], updates: Partial<Omit<Task, 'id'>>) => Promise<void>
}

/**
 * Der Schreibweg des **Aufgaben-Menüs** auf eine Menge von Aufgaben — herausgelöst aus
 * `useContextMenuBadgeUpdate`, damit auch die **Listen-Werkzeug-Pille** (#291) ihn nimmt: Sie schreibt
 * nicht auf die ganze Auswahl, sondern nur auf die Aufgaben, für die ein Befehl gilt.
 */
export function useTaskUpdates({ setOptimisticTaskOverride, logTag }: UseTaskUpdatesParams): TaskUpdates {
  const source = useDataSource()

  const applyWrites = useCallback(async (writes: TaskWrite[]) => {
    if (writes.length === 0) return
    for (const { id, changes } of writes) setOptimisticTaskOverride(id, changes)
    try {
      await source.updateTasks(writes)
    } catch (err) {
      console.error(`[${logTag}] Failed to apply badge update:`, err)
    }
  }, [setOptimisticTaskOverride, logTag, source])

  const applyTo = useCallback(async (ids: readonly string[], updates: Partial<Omit<Task, 'id'>>) => {
    let writes: TaskWrite[] = ids.map(id => ({ id, changes: updates }))
    /*
      **Favorit setzen** (#210): Wer schon einer ist, behält seinen Platz; die übrigen landen in der
      Reihenfolge der Auswahl unten — je eine eigene Stelle, sonst ließen sie sich nie zwischen
      einander ziehen (die Mitte zweier gleicher Werte ist derselbe Wert).
    */
    if ('favoriteOrder' in updates) {
      const rows = await Promise.all(ids.map(id => source.getTask(id)))
      // Unteraufgaben sind keine Favoriten — auch nicht in einer gemischten Auswahl (Demnächst listet sie).
      const topLevel = rows.filter((row): row is Task => !!row && row.parentTaskId == null)
      const start = updates.favoriteOrder
      if (start == null) {
        writes = topLevel.map(row => ({ id: row.id, changes: updates }))
      } else {
        let next = start
        writes = topLevel.flatMap(row => (row.favoriteOrder != null ? [] : [{ id: row.id, changes: { ...updates, favoriteOrder: next++ } }]))
      }
    }
    await applyWrites(writes)
  }, [applyWrites, source])

  return useMemo(() => ({ applyWrites, applyTo }), [applyWrites, applyTo])
}
